import React, { useCallback, useState } from 'react';
import { Root } from './types';
import {
  BadgePercent,
  ChevronUp,
  Info,
  Plane,
  X,
  Receipt,
  Luggage,
  Ban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { FLIGHT_TABS } from '@/lib/contants';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FlightInfo from './FlightInfo';
import FareDetails from './FareDetails';
import BaggageRules from './BaggageRules';
import FareRule from '@/components/fareRule';
import { useFareRuleMutation } from '@flysync/shared-redux/services/booking';
import {
  FormattedFareRuleData,
  formatFareRuleError,
  formatFetchFareRuleResponse,
} from '@/lib/fare-rule-formatter';
import Link from '@/lib/app-link';
import BucketFare from '@/components/modals/BucketFare';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import Image from '@/lib/app-image';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSelectedFlight,
  setSelectedReturnFlight,
} from '@flysync/shared-redux/slices/userInputSlice';
import {
  setSelectBucket,
  setReturnBucket,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { formatCurrency } from '@/lib/utils';
import _ from 'lodash';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import defaultLogo from './../../../assets/airlines/none.png'; // Import default image
import { getAirlineIconUrl } from '@/lib/airlineIcon';
import { format, isValid, parseISO } from 'date-fns';

function FlightListItem({ type, data }: { type: string; data: Root }) {
  const [open, setOpen] = React.useState<boolean>(false);
  const [showBuckerFare, setShowBucketFare] = React.useState<boolean>(false);
  const flightSearchInput = useSelector((state: any) => state.flightSearch);
  const [fareRuleData, setFareRuleData] =
    useState<FormattedFareRuleData | null>(null);
  const [fareRuleLoader, setFareRuleLoader] = useState(false);
  const [getFareRule] = useFareRuleMutation();
  const dispatch = useDispatch();
  const userInput = useSelector((state: any) => state.userInputs);
  const [imgError, setImgError] = useState(false);
  const selectedFlight =
    type === 'OUT_BOUND'
      ? userInput.selectedFlight
      : userInput.selectedReturnFlight;
  const [showMobileModal, setShowMobileModal] = useState(false);

  const onSelectBook = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (
      flightSearchInput?.travelType === 'OW' ||
      flightSearchInput?.tripType == 'INRT'
    ) {
      setShowBucketFare(!showBuckerFare);
    }
    dispatch(setSelectedFlight(data));
    dispatch(setSelectBucket(data.price_details));
    // sessionStorage.setItem("USER_INPUT",JSON.stringify(data))
  };

  const toggleCollapsible = () => {
    if (flightSearchInput?.travelType === 'OW') {
      setOpen(!open);
    } else {
      setOpen(!open);
    }
  };

  const requiredDocument = {
    Student: [
      'Valid Student ID Card',
      'School/College/University Bonafide Certificate',
      'Age should be between 12 to 25 years',
    ],
    Senior_Citizen: [
      'Valid ID proof showing Date of Birth',
      'Age should be 60 years or above',
      'Passport/Driving License/Senior Citizen Card',
    ],
    Armed_Forces: [
      'Valid Military ID Card',
      'Dependent Card (for family members)',
      'Discharge Book (for retired personnel)',
      'Canteen Card',
    ],
    Doctor_Nurses: [
      'Valid Medical Registration Certificate',
      'Doctor ID Card from Hospital/Clinic',
      'Government Medical Council Registration',
    ],
  };

  const FareType = [
    'Student',
    'Senior_Citizen',
    'Armed_Forces',
    'Doctors_Nurses',
  ];

  const handleRadioChange = () => {
    if (type === 'OUT_BOUND') {
      dispatch(setSelectedFlight(data));
      dispatch(setSelectBucket(data.price_details));
    } else {
      dispatch(setSelectedReturnFlight(data));
      dispatch(setReturnBucket(data.price_details));
    }
  };

  const useScreenSize = () => {
    const [screenSize, setScreenSize] = React.useState({
      isMobile: false,
    });

    React.useEffect(() => {
      const handleResize = () => {
        const width = window.innerWidth;
        setScreenSize({
          isMobile: width < 640,
        });
      };

      // Initial check
      handleResize();

      // Add event listener
      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return screenSize;
  };

  const { isMobile } = useScreenSize();

  // Helper function to safely calculate date difference
  const getDateDifference = (
    departureDate: string,
    arrivalDate: string,
  ): number => {
    try {
      if (!departureDate || !arrivalDate) return 0;

      const depDate = parseISO(departureDate);
      const arrDate = parseISO(arrivalDate);

      if (!isValid(depDate) || !isValid(arrDate)) return 0;

      return Math.floor(
        (arrDate.getTime() - depDate.getTime()) / (1000 * 60 * 60 * 24),
      );
    } catch (error) {
      console.warn('Date calculation error:', error);
      return 0;
    }
  };

  // Helper function to safely format dates
  const safeFormatDate = (dateString: string, formatString: string): string => {
    try {
      if (!dateString) return '';

      const date = parseISO(dateString);
      if (!isValid(date)) return '';

      return format(date, formatString);
    } catch (error) {
      console.warn('Date formatting error:', error);
      return '';
    }
  };

  const hasFareRuleData = (fareRule: any) => {
    return !!fareRule && Object.keys(fareRule).length > 0;
  };

  const fetchFareRuleData = useCallback(
    async (flight: any) => {
      const embeddedFareRule = flight?.price_details?.[0]?.fareRule;
      const priceDetails = flight?.price_details?.[0];

      if (hasFareRuleData(embeddedFareRule) || fareRuleData || fareRuleLoader) {
        return;
      }

      if (!flight || !priceDetails) {
        return;
      }

      setFareRuleLoader(true);

      try {
        const response = await getFareRule({
          data: {
            flowType: 'search',
            data: [
              {
                airline_code: flight?.airline_code,
                airline_logo: flight?.airline_logo,
                airline_name: flight?.airline_name,
                flight_number: flight?.flight_number,
                origin: flight?.origin,
                destination: flight?.destination,
                departure_time: flight?.departure_time,
                departure_date: flight?.departure_date,
                departure_terminal:
                  flight?.departure_terminal ?? flight?.origin_terminal,
                duration_stops: flight?.duration_stops,
                stop_count: flight?.stop_count,
                travel_time: flight?.travel_time,
                arrival_time: flight?.arrival_time,
                arrival_date: flight?.arrival_date,
                arrival_terminal:
                  flight?.arrival_terminal ?? flight?.destination_terminal,
                travel_date: flight?.travel_date,
                origin_airport: flight?.origin_airport,
                destination_airport: flight?.destination_airport,
                supplier: priceDetails?.supplier_code,
                travel_type: flight?.trip_type ?? flightSearchInput?.travelMode,
                trip_type: flightSearchInput?.tripType,
                cabin_type: flightSearchInput?.passengers?.cabinId ?? 'EC',
                fareTypeId: priceDetails?.fareTypeId,
                refundType: priceDetails?.refundableType,
                departureDate: flight?.departure_date,
                bookingClass: priceDetails?.popover_info?.[1]?.value ?? '',
                fareBasis: priceDetails?.popover_info?.[2]?.value ?? '',
                price_details: priceDetails,
              },
            ],
          },
        }).unwrap();

        setFareRuleData(formatFetchFareRuleResponse(response));
      } catch {
        setFareRuleData(formatFareRuleError());
      } finally {
        setFareRuleLoader(false);
      }
    },
    [
      fareRuleData,
      fareRuleLoader,
      flightSearchInput?.passengers?.cabinId,
      flightSearchInput?.travelMode,
      flightSearchInput?.tripType,
      getFareRule,
    ],
  );

  return (
    <>
      <Collapsible open={open} onOpenChange={toggleCollapsible}>
        <div className="mt-2 rounded-xl border sm:mt-3 lg:mt-4">
          <div
            className={`duration-250 relative w-full rounded-xl bg-white transition ease-in-out hover:bg-background ${open ? 'rounded-b-none' : 'rounded-xl'} p-1 sm:p-2 lg:p-3`}
          >
            {/* Mobile View */}
            <div className="sm:hidden">
              {data?.flightInfo?.map((item, index) => (
                <div
                  key={`mobile-${item?.flight_number}-${index}`}
                  className="flex flex-col gap-3"
                  onClick={(e) => (isMobile ? onSelectBook(e) : null)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full">
                        <Image
                          alt="airline_logos"
                          src={
                            imgError
                              ? defaultLogo
                              : getAirlineIconUrl(item?.airline_code)
                          }
                          crossOrigin="anonymous"
                          onError={() => setImgError(true)}
                          className="h-full w-full rounded-full object-contain"
                        />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-zinc-800">
                          {item?.airline_name}
                        </h4>
                        <p className="text-xs text-text_secondary">
                          {item?.flight_number}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <h4 className="text-sm font-bold text-zinc-800">
                        {formatCurrency(
                          Number(
                            data?.price_details?.[0]?.fareDetails?.net_fare ||
                              0,
                          ),
                        )}
                      </h4>
                    </div>
                  </div>

                  <div className={`flex items-center justify-between text-sm`}>
                    <div className="text-center">
                      <p className="font-bold text-zinc-800">
                        {item?.departure_time}
                      </p>
                      <p className="text-xs text-text_secondary">
                        {item?.origin}
                      </p>
                    </div>
                    <div className="flex flex-col items-center px-2">
                      <p className="text-xs text-text_secondary">
                        {item?.duration}
                      </p>
                      <div className="relative w-16 py-0.5">
                        <div className="absolute top-1/2 w-full border-t border-text_secondary"></div>
                        {item?.stopCount > 0 && (
                          <div className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary bg-white" />
                        )}
                      </div>
                      <p className="text-xs text-text_secondary">
                        {item?.stops}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="relative font-bold text-zinc-800">
                        {item?.arrival_time}
                        {item?.departure_date !== item?.arrival_date && (
                          <span
                            className={`absolute -right-2 -top-2 text-[10px] font-semibold text-red-500 ${isMobile ? 'right-0' : ''}`}
                          >
                            +
                            {getDateDifference(
                              item?.departure_date,
                              item?.arrival_date,
                            )}
                            D
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-text_secondary">
                        {item?.destination}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tablet View */}
            <div className="hidden sm:block lg:hidden">
              {data?.flightInfo?.map((item, index) => (
                <div
                  key={`tablet-${item?.flight_number}-${index}`}
                  className="flex items-center justify-between gap-4"
                >
                  <div className="flex basis-[30%] items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full">
                      <Image
                        alt="airline_logos"
                        src={
                          imgError
                            ? defaultLogo
                            : getAirlineIconUrl(item?.airline_code)
                        }
                        crossOrigin="anonymous"
                        onError={() => setImgError(true)}
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-zinc-800">
                        {item?.airline_name}
                      </h4>
                      <p className="text-sm text-text_secondary">
                        {item?.flight_number}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-1 items-center justify-between">
                    <div className="text-center">
                      <p className="text-base font-bold text-zinc-800">
                        {item?.departure_time}
                      </p>
                      <p className="text-xs text-text_secondary">
                        {item?.origin}
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <p className="text-sm text-text_secondary">
                        {item?.duration}
                      </p>
                      <div className="relative w-20 py-0.5">
                        <div className="absolute top-1/2 w-full border-t-2 border-text_secondary"></div>
                        {item?.stopCount > 0 && (
                          <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary bg-white" />
                        )}
                      </div>
                      <p className="text-sm text-text_secondary">
                        {item?.stops}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="relative text-base font-bold text-zinc-800">
                        {item?.arrival_time}
                        {item?.departure_date !== item?.arrival_date && (
                          <span className="absolute -right-2 -top-2 text-xs font-semibold text-red-500">
                            +
                            {getDateDifference(
                              item?.departure_date,
                              item?.arrival_date,
                            )}
                            D
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-text_secondary">
                        {item?.destination}
                      </p>
                    </div>
                    <div className="text-right">
                      <h4 className="text-base font-bold text-zinc-800">
                        {formatCurrency(
                          Math.abs(
                            Number(
                              data?.price_details?.[0]?.fareDetails?.net_fare,
                            ) 
                          ),
                          // -
                          //     Number(
                          //       data?.price_details?.[0]?.fareDetails
                          //         ?.management_fee,
                          //     ) -
                          //     Number(
                          //       data?.price_details?.[0]?.fareDetails
                          //         ?.management_fee_tax,
                          //     ),
                        )}
                      </h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop View */}
            <div className="hidden lg:block">
              <div
                className={`flex gap-10 ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.tripType == 'INRT' ? 'items-end' : 'items-center'}`}
              >
                <div className="flex w-full flex-col justify-between">
                  {data?.flightInfo?.map((item, index) => (
                    <React.Fragment
                      key={`info-${item?.flight_number}-${index}`}
                    >
                      <div className="ld:my-4 my-2 flex w-full flex-1 flex-row justify-between gap-1 sm:my-2 md:my-3">
                        <div className="basis-[30%]">
                          <div className="flex flex-row items-center">
                            <div className="flex items-center justify-center rounded-full text-white sm:h-4 sm:w-4 md:h-8 md:w-8 lg:h-10 lg:w-10">
                              <Image
                                alt="airline_logos"
                                width={50}
                                height={50}
                                crossOrigin="anonymous"
                                src={
                                  imgError
                                    ? defaultLogo
                                    : getAirlineIconUrl(item?.airline_code)
                                }
                                onError={() => setImgError(true)}
                                className="rounded-full"
                              />
                            </div>
                            <div
                              className={`flex flex-col items-start ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.travelMode == 'DO' ? 'ml-1' : 'ml-4'}`}
                            >
                              <h4
                                className={`text-start font-bold text-zinc-800 ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.travelMode == 'DO' ? 'text-sm' : 'sm:text-base'}`}
                              >
                                {item?.airline_name}
                                <p
                                  className={`text-start text-[12px] font-medium text-zinc-600`}
                                >
                                  {item?.flight_number}
                                </p>
                              </h4>
                            </div>
                          </div>
                        </div>
                        <div className="flex basis-[12%] flex-col items-center justify-center">
                          <p className={`text-[12px] text-zinc-600`}>
                            {item?.departure_city_name}({item?.origin})
                          </p>
                          <h2
                            className={`font-bold text-zinc-800 ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.travelMode == 'DO' ? 'text-base' : 'sm:text-sm md:text-lg lg:text-3xl'}`}
                          >
                            {item?.departure_time}
                          </h2>
                          <p className={`text-[10px]`}>
                            {safeFormatDate(
                              item?.departure_date,
                              'MMM dd,yyyy',
                            )}{' '}
                          </p>
                        </div>
                        <div className="flex basis-[12%] flex-col items-center justify-center">
                          <h4
                            className={`font-semibold text-text_secondary ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.travelMode == 'DO' ? 'text-xs' : 'sm:text-xs md:text-sm'}`}
                          >
                            {item?.duration}
                          </h4>
                          <div className="relative w-16 py-0.5">
                            <div className="absolute top-1/2 w-full border-t-2 border-text_secondary"></div>
                            {item?.stopCount > 0 &&
                              [...Array(item?.stopCount)].map((_, index) => (
                                <div
                                  key={index}
                                  className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary bg-white"
                                  style={{
                                    left: `50%`,
                                  }}
                                />
                              ))}
                          </div>
                          <h4
                            className={`text-[10px] font-bold text-text_secondary`}
                          >
                            {item?.stops}
                          </h4>
                        </div>
                        <div className="flex basis-[12%] flex-col items-center justify-center">
                          <p className={`text-[12px] text-zinc-600`}>
                            {item?.arrival_city_name}({item?.destination})
                          </p>
                          <h2
                            className={`relative font-bold text-zinc-800 ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.travelMode == 'DO' ? 'text-base' : 'sm:text-sm md:text-lg lg:text-3xl'}`}
                          >
                            {item?.arrival_time}
                            {item?.departure_date !== item?.arrival_date && (
                              <span className="absolute -right-5 -top-1 text-xs font-semibold text-red-500">
                                +
                                {getDateDifference(
                                  item?.departure_date,
                                  item?.arrival_date,
                                )}
                                D
                              </span>
                            )}
                          </h2>
                          <p className={`text-[10px]`}>
                            {safeFormatDate(
                              item?.arrival_date,
                              'MMM dd,yyyy',
                            )}{' '}
                          </p>
                        </div>
                        <div
                          className={`flex w-full basis-[120px] items-center justify-start ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.travelMode == 'DO' ? 'justify-end' : 'ml-10'}`}
                        >
                          {(flightSearchInput?.travelType === 'RT' ||
                            flightSearchInput?.travelType === 'OW') &&
                            (flightSearchInput?.tripType !== 'INRT' ||
                              (flightSearchInput?.tripType === 'INRT' &&
                                index !== 0)) && (
                              <div className="relative">
                                <h2
                                  className={`relative font-bold text-zinc-800 ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.travelMode == 'DO' ? 'text-base' : 'sm:text-sm md:text-lg lg:text-3xl'}`}
                                >
                                  {formatCurrency(
                                    Number(
                                      data?.price_details[0]?.fareDetails
                                        ?.net_fare,
                                    )
                                  )}

                                  {
                                     /* -
                                      Number(
                                        data?.price_details[0]?.fareDetails
                                          ?.admin_markup,
                                      ) -
                                      Number(
                                        data?.price_details[0]?.fareDetails
                                          ?.admin_markup_gst,
                                      ), */
                                  }

                                </h2>
                                <div className="absolute right-0 flex w-max items-center justify-end text-[10px] text-red-400">
                                  {FareType.includes(
                                    data?.price_details[0]?.fareType,
                                  ) && (
                                    <TooltipProvider>
                                      <Tooltip delayDuration={300}>
                                        <TooltipTrigger>
                                          <Info className="h-3 w-3" />
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="left"
                                          className="max-w-[300px] bg-background text-left"
                                        >
                                          <ul>
                                            {data?.price_details[0]?.fareType &&
                                              requiredDocument[
                                                data?.price_details[0]
                                                  ?.fareType as keyof typeof requiredDocument
                                              ] && (
                                                <div className="text-xs">
                                                  <p className="font-semibold">
                                                    {_.startCase(
                                                      data?.price_details[0]
                                                        ?.fareType,
                                                    )}{' '}
                                                    Required Documents
                                                  </p>
                                                  <ul className="list-disc pl-4 text-gray-600">
                                                    {requiredDocument[
                                                      data?.price_details[0]
                                                        ?.fareType as keyof typeof requiredDocument
                                                    ].map(
                                                      (
                                                        doc: string,
                                                        index: number,
                                                      ) => (
                                                        <li key={index}>
                                                          {doc}
                                                        </li>
                                                      ),
                                                    )}
                                                  </ul>
                                                </div>
                                              )}
                                          </ul>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}{' '}
                                  {_.startCase(
                                    data?.price_details[0]?.fareType,
                                  )}{' '}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {flightSearchInput?.travelType === 'OW' ||
                flightSearchInput?.tripType == 'INRT' ? (
                  <Button
                    variant="outline"
                    className={`bg-primary text-center sm:px-4 sm:py-2 md:px-6 md:py-3 lg:px-8 lg:py-5 ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.tripType == 'INRT' ? 'mb-[1.5rem]' : ''}`}
                    onClick={(e) => onSelectBook(e)}
                  >
                    <h4 className="text-white sm:text-xs md:text-sm lg:text-base">
                      View Price
                    </h4>
                  </Button>
                ) : (
                  <RadioGroup
                    value={selectedFlight.flight_number}
                    onValueChange={handleRadioChange}
                  >
                    <RadioGroupItem value={data.flight_number} id="selected" />
                    <Label htmlFor="selected" />
                  </RadioGroup>
                )}
              </div>
            </div>

            {/* Common Footer */}
            <div className="flex items-center justify-between px-3">
              <CollapsibleTrigger
                className="cursor-pointer sm:w-fit md:w-full lg:w-full"
                onClick={(e) => {
                  if (window.innerWidth < 640) {
                    e.preventDefault();
                    setShowMobileModal(true);
                  }
                }}
              >
                <div
                  className={`fo flex items-center justify-center gap-1 py-1 text-xs text-[#0B6FD6] sm:text-sm md:justify-end ${flightSearchInput?.travelType === 'RT' && flightSearchInput?.travelMode == 'DO' && isMobile ? 'hidden' : 'block'} `}
                >
                  Flight Details
                  <ChevronUp
                    className={`h-4 w-4 transform transition-all duration-700 sm:h-5 sm:w-5 ${open ? '' : 'rotate-180'} `}
                  />
                </div>
              </CollapsibleTrigger>

              {flightSearchInput?.travelType === 'OW' ||
              flightSearchInput?.tripType == 'INRT' ? (
                <h4
                  className="text-xs text-primary sm:text-white md:hidden lg:hidden"
                  onClick={(e) => onSelectBook(e)}
                >
                  View Price
                </h4>
              ) : (
                <RadioGroup
                  value={selectedFlight.flight_number}
                  onValueChange={handleRadioChange}
                  className="md:hidden lg:hidden"
                >
                  <RadioGroupItem value={data.flight_number} id="selected" />
                  <Label htmlFor="selected" />
                </RadioGroup>
              )}
            </div>
          </div>
        </div>
        {/* Mobile Popup Modal */}
        {showMobileModal && (
          <div className="fixed inset-0 z-50 flex items-end bg-black/50 sm:hidden">
            <div className="max-h-[80vh] w-full overflow-y-auto rounded-t-xl bg-white duration-300 animate-in slide-in-from-bottom">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b p-4">
                <h3 className="text-lg font-semibold">Flight Details</h3>
                <X
                  className="h-6 w-6 text-gray-500"
                  onClick={() => setShowMobileModal(false)}
                />
              </div>

              {/* Modal Content */}
              <div className="max-h-[80vh] p-3 sm:max-h-[85vh] sm:p-4 lg:max-h-[90vh] lg:p-5">
                <Tabs defaultValue="Flight Information" className="w-full">
                  <div className="relative">
                    {/* Gradient Shadows for Scroll Indication */}
                    <div className="absolute bottom-0 left-0 top-0 z-10 w-3 bg-gradient-to-r from-white to-transparent sm:w-4 lg:w-5" />
                    <div className="absolute bottom-0 right-0 top-0 z-10 w-3 bg-gradient-to-l from-white to-transparent sm:w-4 lg:w-5" />

                    {/* Scrollable TabsList Container */}
                    <div className="w-full overflow-hidden">
                      <div className="relative -mx-3 overflow-x-auto overflow-y-hidden sm:-mx-4 lg:-mx-5">
                        <TabsList className="scrollbar-hide mx-auto flex w-max snap-x snap-mandatory space-x-4 scroll-smooth px-3 py-1 sm:space-x-6 sm:px-4 sm:py-1.5 lg:space-x-8 lg:px-5 lg:py-2">
                          {FLIGHT_TABS?.map((item) => (
                            <TabsTrigger
                              key={item?.id}
                              className="flex min-w-[64px] flex-shrink-0 snap-start flex-col items-center justify-center rounded-lg border-b-2 border-transparent py-1.5 font-semibold text-zinc-800 transition-all duration-200 hover:bg-gray-50 data-[state=active]:border-primary sm:min-w-[72px] sm:py-2 lg:min-w-[80px] lg:py-2.5"
                              value={item?.value}
                              onClick={() => {
                                if (item?.value === 'Cancellation Rules') {
                                  fetchFareRuleData(data);
                                }
                              }}
                            >
                              {/* Label */}
                              <span className="whitespace-nowrap text-[10px] font-medium text-gray-700 group-hover:text-primary sm:text-xs sm:font-semibold lg:text-sm">
                                {item?.value}
                              </span>
                            </TabsTrigger>
                          ))}
                        </TabsList>
                      </div>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 mt-3 max-h-[calc(80vh-120px)] overflow-y-auto sm:mt-4 sm:max-h-[calc(85vh-130px)] lg:mt-5 lg:max-h-[calc(90vh-140px)]">
                    <TabsContent value="Flight Information">
                      <FlightInfo
                        layoverStopsData={data?.layoverStopsData}
                        layoverData={data?.layover}
                      />
                    </TabsContent>
                    <TabsContent value="Fare Details">
                      <FareDetails
                        data={data?.price_details?.[0]?.fareDetails}
                      />
                    </TabsContent>
                    <TabsContent value="Baggage Rules">
                      <BaggageRules
                        data={{
                          ...data?.flightInfo?.[0],
                          ...data?.price_details[0],
                        }}
                      />
                    </TabsContent>
                    <TabsContent
                      value="Cancellation Rules"
                      onClick={() => fetchFareRuleData(data)}
                    >
                      <FareRule
                        fareRules={
                          hasFareRuleData(data?.price_details?.[0]?.fareRule)
                            ? data?.price_details?.[0]?.fareRule
                            : fareRuleData
                        }
                        isLoader={
                          !hasFareRuleData(
                            data?.price_details?.[0]?.fareRule,
                          ) && !fareRuleData
                            ? true
                            : fareRuleLoader
                        }
                      />
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            </div>
          </div>
        )}

        {/* Desktop/Tablet Collapsible Content */}
        <CollapsibleContent className="hidden transform rounded-b-xl bg-white transition-all duration-700 sm:block">
          <div
            className={`${flightSearchInput?.travelType === 'RT' ? 'm-0' : 'lg:-6 mx-2 sm:mx-0 md:mx-4'}`}
          >
            <Tabs defaultValue="Flight Information">
              <TabsList
                className={`border-light flex justify-between border-b-4 ${flightSearchInput?.travelType === 'RT' ? 'm-0' : 'lg:-6 mx-2 sm:mx-0 md:mx-4'}`}
              >
                {FLIGHT_TABS?.map((item) => (
                  <TabsTrigger
                    key={item?.id}
                    className={`border-light -mb-[8px] items-center border-b-4 pb-2 font-semibold text-zinc-800 data-[state=active]:border-primary sm:px-2 sm:py-0.5 md:px-3 md:py-1 ${flightSearchInput?.travelType === 'RT' ? 'text-xs' : 'text-sm'}`}
                    value={item?.value}
                    onClick={() => {
                      if (item?.value === 'Cancellation Rules') {
                        fetchFareRuleData(data);
                      }
                    }}
                  >
                    {item?.value}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="Flight Information" className="pb-5">
                <FlightInfo
                  layoverStopsData={data?.layoverStopsData}
                  layoverData={data?.layover}
                />
              </TabsContent>
              <TabsContent value="Fare Details" className="mr-4 pb-5">
                <FareDetails data={data?.price_details?.[0]?.fareDetails} />
              </TabsContent>
              <TabsContent value="Baggage Rules" className="pb-5">
                <BaggageRules
                  data={{
                    ...data?.flightInfo?.[0],
                    ...data?.price_details?.[0],
                  }}
                />
              </TabsContent>
              <TabsContent
                value="Cancellation Rules"
                className="pb-5"
                onClick={() => fetchFareRuleData(data)}
              >
                <FareRule
                  fareRules={
                    hasFareRuleData(data?.price_details?.[0]?.fareRule)
                      ? data?.price_details?.[0]?.fareRule
                      : fareRuleData
                  }
                  isLoader={
                    !hasFareRuleData(data?.price_details?.[0]?.fareRule) &&
                    !fareRuleData
                      ? true
                      : fareRuleLoader
                  }
                />
              </TabsContent>
            </Tabs>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <BucketFare showModal={showBuckerFare} setShowModal={setShowBucketFare} />
    </>
  );
}

export default FlightListItem;
